import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Grid } from '@mui/material';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { DefaultPageProps } from '@/types/types/page/pages';
import {
    IWalletPaymentAllDetails,
    IWalletPaymentDetailsRequestData,
    IAccount,
    IWalletUploadedDocument,
} from '@/types/types/forms/wallet/wallet.interface';
import { PreviousNextButtons } from '@/components/forms/other/PreviousNextButtons/PreviousNextButtons';
import { CenteredLoadingSpinner } from '@/components/general/CenteredLoadingSpinner/CenteredLoadingSpinner';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { WalletPaymentSteps } from '@/types/enums/forms/wallet/walletPaymentSteps';
import { walletService } from '@/services/api/walletService/walletService';
import { useZipLocations } from '@/hooks/forms/useZipLocations';
import { isArrayWithValues } from '@/utils/fp/array/array';
import { isEmpty } from '@/utils/fp/isEmpty/isEmpty';
import {
    WALLET_DETAILS_FILE_UPLOAD_FIELDS,
    WalletPaymentDetailsFields,
} from '@/components/blocks/wallet/Payment/WalletPaymentDetailsFields/WalletPaymentDetailsFields';
import { PaymentAmount } from '@/components/blocks/wallet/PaymentAmount/PaymentAmount';
import { NewFetchErrorFormat } from '@/types/types/general/errors';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';

interface WalletPaymentDetailsFormProps {
    data: IWalletPaymentAllDetails;
    dictionary: DefaultPageProps['dictionary'];
    selectedAccount: IAccount;
    updateWalletPaymentData: (data: Partial<IWalletPaymentAllDetails>) => void;
    documents: Record<string, IWalletUploadedDocument[]>;
    updatePaymentDocumentsById: (id: string, newDocuments: IWalletUploadedDocument[]) => void;
}

export const WalletPaymentDetailsForm = ({
    data,
    dictionary,
    selectedAccount,
    updateWalletPaymentData,
    documents,
    updatePaymentDocumentsById,
}: WalletPaymentDetailsFormProps) => {
    const { t } = usePhrase();
    const initialCachedData = data.cachedData[WalletPaymentSteps.ProvidePaymentDetails];
    const documentsRef = useRef(documents);

    useEffect(() => {
        documentsRef.current = documents;
    }, [documents]);

    const {
        fields: dataFields,
        states,
        vatAmount,
    } = data.data[WalletPaymentSteps.ProvidePaymentDetails];
    const { amount: amountVal, existingPaymentAmount } =
        data.cachedData[WalletPaymentSteps.PaymentStart];

    const defaultValues = useMemo(() => {
        if (!data || !isArrayWithValues(dataFields)) return {};
        return dataFields.reduce((acc, field) => {
            if (!WALLET_DETAILS_FILE_UPLOAD_FIELDS.includes(field.id)) {
                return { ...acc, [field.id]: field.value ?? undefined };
            }
            return acc;
        }, {});
    }, [data, dataFields]);

    const fileUploadFields = useMemo(
        () =>
            WALLET_DETAILS_FILE_UPLOAD_FIELDS.reduce(
                (acc, fieldId) => ({
                    ...acc,
                    [fieldId]: documentsRef.current[fieldId] || undefined,
                }),
                {},
            ),
        [],
    );

    const values = useMemo(
        () => ({
            ...(!isEmpty(initialCachedData) ? initialCachedData : defaultValues),
            ...fileUploadFields,
        }),
        [initialCachedData, defaultValues, fileUploadFields],
    );

    const { handleSubmit, control, watch, setValue, getValues, formState, setError, clearErrors } =
        useForm<Partial<IWalletPaymentDetailsRequestData>>({
            mode: 'onChange',
            values,
        });

    useEffect(() => {
        documentsRef.current = documents;
        WALLET_DETAILS_FILE_UPLOAD_FIELDS.forEach((fieldId) => {
            setValue(fieldId, documents[fieldId] || undefined);
        });
    }, [documents, setValue]);

    const { errors, isSubmitting } = formState;

    const [zipValue, countryCodeValue] = watch(['zip', 'countryCode']) as [string, string];

    const { statesList, citiesList, onZipChange } = useZipLocations<
        Partial<IWalletPaymentDetailsRequestData>
    >({
        zipValue,
        countryCodeValue,
        setValue,
    });

    const { handleServerErrors, serverErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const returnToRequestStep = () =>
        updateWalletPaymentData({ currentStep: WalletPaymentSteps.PaymentStart });

    const excludeFieldsFromFormValues = <T extends Record<string, unknown>, K extends keyof T>(
        formValues: T,
        excludeFields: K[],
    ): Omit<T, K> => {
        const result: Partial<T> = {};
        for (const key in formValues) {
            if (!excludeFields.includes(key as keyof T as K)) {
                result[key] = formValues[key];
            }
        }
        return result as Omit<T, K>;
    };

    const getRequiredFileFieldIds = (data: IWalletPaymentAllDetails): string[] => {
        const detailsStep = data.data[WalletPaymentSteps.ProvidePaymentDetails];
        if (!detailsStep || !detailsStep.fields) {
            return [];
        }
        return detailsStep.fields
            .filter(
                (field) => field.required && WALLET_DETAILS_FILE_UPLOAD_FIELDS.includes(field.id),
            )
            .map((field) => field.id);
    };

    const handleErrors = (missingFileFieldValues?: string[], err?: unknown) => {
        const errors: NewFetchErrorFormat = (err as NewFetchErrorFormat) || { errors: [] };
        if (!errors.errors) {
            errors.errors = [];
        }
        missingFileFieldValues?.forEach((field) => {
            errors.errors.push({
                message: 'Missing',
                property: field,
                phraseId: WALLET_PHRASES.MISSING_FIELD_ERROR,
            });
        });
        handleServerErrors(errors);
    };

    const handleSubmitData = async (formValues: Partial<IWalletPaymentDetailsRequestData>) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { lastPaymentMethodId, paymentMethodId, paymentMethodPhraseId, ...paymentStartData } =
            data.cachedData[WalletPaymentSteps.PaymentStart];

        const formValuesToPass = excludeFieldsFromFormValues(
            formValues,
            WALLET_DETAILS_FILE_UPLOAD_FIELDS,
        );

        const formData = Object.assign({}, paymentStartData, {
            ...formValuesToPass,
            paymentMethodId: lastPaymentMethodId || paymentMethodId,
        });

        const requiredFileFields = getRequiredFileFieldIds(data);

        const missingFileFieldValues = requiredFileFields.filter((field) => {
            const value = getValues(field);
            return !value || !isArrayWithValues(value);
        });

        try {
            if (!selectedAccount?.accountId) {
                throw new Error('Account id is not provided');
            }

            const responseData = await walletService.submitPaymentDetails(
                selectedAccount.accountId,
                formData,
            );

            if (isArrayWithValues(missingFileFieldValues)) {
                missingFileFieldValues?.forEach((field) => {
                    setError(field, {
                        type: 'custom',
                        message: t(WALLET_PHRASES.MISSING_FIELD_ERROR, { asString: true }),
                    });
                });
                return;
            }

            const { nextStep, ...currentStepData } = responseData;
            updateWalletPaymentData({
                currentStep: nextStep,
                prevStep: WalletPaymentSteps.ProvidePaymentDetails,
                cachedData: {
                    ...data.cachedData,
                    [WalletPaymentSteps.ProvidePaymentDetails]: { ...formValuesToPass },
                },
                data: { ...data.data, [nextStep]: { ...currentStepData } },
            });
        } catch (err) {
            handleErrors(missingFileFieldValues, err);
        }
    };

    return (
        <>
            {dataFields ? (
                <form onSubmit={handleSubmit(handleSubmitData)} noValidate>
                    <Grid container spacing={3} sx={{ mb: 2 }}>
                        <FormGridErrors
                            errors={serverErrors}
                            generalErrorId="server_error_message_box"
                        />
                        <Grid size={12} sx={{ mb: 1 }}>
                            <PaymentAmount
                                transactionData={selectedAccount}
                                amount={amountVal}
                                vatAmount={vatAmount}
                                existingPaymentAmount={existingPaymentAmount}
                            />
                        </Grid>
                    </Grid>
                    <WalletPaymentDetailsFields
                        dataFields={dataFields}
                        dictionary={dictionary}
                        states={states}
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        statesList={statesList}
                        citiesList={citiesList}
                        onZipChange={onZipChange}
                        documents={documents}
                        updatePaymentDocumentsById={updatePaymentDocumentsById}
                    />
                    <Grid
                        container
                        sx={{
                            justifyContent: 'flex-end',
                            marginTop: 2,
                        }}
                    >
                        <PreviousNextButtons
                            onPreviousClicked={returnToRequestStep}
                            nextDisabled={isSubmitting}
                            previousDisabled={isSubmitting}
                            prevLabel={WALLET_PHRASES.BACK_BUTTON}
                            nextLabel={WALLET_PHRASES.SUBMIT_BUTTON}
                        />
                    </Grid>
                </form>
            ) : (
                <CenteredLoadingSpinner />
            )}
        </>
    );
};
